import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, RotateCcw } from "lucide-react";

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number[];
  type: string;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  questions: QuizQuestion[];
  passingScore: number;
}

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  quiz: Quiz | null;
  isAdmin?: boolean;
  onQuizComplete?: (score: number, isPassed: boolean, answers: number[][]) => void;
}

export default function QuizModal({ 
  isOpen, 
  onClose, 
  quiz, 
  isAdmin = false, 
  onQuizComplete 
}: QuizModalProps) {
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[][]>([]);
  const [showResults, setShowResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [isPassed, setIsPassed] = useState(false);
  const [showAnswers, setShowAnswers] = useState(isAdmin); // Show answers immediately for admin

  // Reset state when quiz changes or modal opens
  useEffect(() => {
    if (isOpen && quiz) {
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setShowResults(false);
      setQuizScore(0);
      setIsPassed(false);
      setShowAnswers(isAdmin);
    }
  }, [isOpen, quiz?.id, isAdmin]);

  if (!isOpen || !quiz) return null;

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;

  const handleAnswerToggle = (optionIndex: number) => {
    if (showResults) return; // Don't allow changes after submission

    setSelectedAnswers(prev => {
      const newAnswers = [...prev];
      if (!newAnswers[currentQuestionIndex]) {
        newAnswers[currentQuestionIndex] = [];
      }
      
      const currentAnswers = newAnswers[currentQuestionIndex];
      if (currentAnswers.includes(optionIndex)) {
        // Remove if already selected
        newAnswers[currentQuestionIndex] = currentAnswers.filter(i => i !== optionIndex);
      } else {
        // Add if not selected
        newAnswers[currentQuestionIndex] = [...currentAnswers, optionIndex];
      }
      
      return newAnswers;
    });
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      calculateScore();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const calculateScore = () => {
    let correctAnswers = 0;
    
    quiz.questions.forEach((question, index) => {
      const userAnswers = selectedAnswers[index] || [];
      let correctAnswer = question.correctAnswer;
      
      // Normalize correctAnswer to always be an array
      if (!Array.isArray(correctAnswer)) {
        correctAnswer = [correctAnswer];
      }
      
      // Check if arrays are equal (same length and same elements)
      // User gets 100% only if they select EXACTLY the correct answers, no more, no less
      const sortedUserAnswers = [...userAnswers].sort((a, b) => a - b);
      const sortedCorrectAnswers = [...correctAnswer].sort((a, b) => a - b);
      
      const isCorrect = sortedUserAnswers.length === sortedCorrectAnswers.length &&
        sortedUserAnswers.every((answer, idx) => answer === sortedCorrectAnswers[idx]);
      
      if (isCorrect) {
        correctAnswers++;
      }
    });

    const score = Math.round((correctAnswers / quiz.questions.length) * 100);
    const passed = score >= quiz.passingScore;
    
    setQuizScore(score);
    setIsPassed(passed);
    setShowResults(true);

    // Call completion callback - always call completion regardless of score
    if (onQuizComplete) {
      onQuizComplete(score, passed, selectedAnswers);
    }

    // Show appropriate toast based on pass/fail
    if (passed) {
      toast({
        title: "Quiz Passed! 🎉",
        description: `Congratulations! You scored ${score}% and passed the quiz.`,
        variant: "default",
      });
    } else {
      toast({
        title: "Quiz Failed",
        description: `You scored ${score}%. You selected incorrect answer(s). Please review and try again.`,
        variant: "destructive",
      });
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setQuizScore(0);
    setIsPassed(false);
    setShowAnswers(isAdmin); // Reset to admin state
  };

  const isAnswerSelected = (optionIndex: number) => {
    return selectedAnswers[currentQuestionIndex]?.includes(optionIndex) || false;
  };

  const isAnswerCorrect = (optionIndex: number) => {
    if (!showResults) return false;
    const correctAnswers = Array.isArray(currentQuestion.correctAnswer) 
      ? currentQuestion.correctAnswer 
      : [currentQuestion.correctAnswer];
    return correctAnswers.includes(optionIndex);
  };

  const isUserAnswerCorrect = () => {
    if (!showResults) return false;
    const userAnswers = selectedAnswers[currentQuestionIndex] || [];
    const correctAnswer = Array.isArray(currentQuestion.correctAnswer) 
      ? currentQuestion.correctAnswer 
      : [currentQuestion.correctAnswer];
    
    return userAnswers.length === correctAnswer.length &&
      userAnswers.every(answer => correctAnswer.includes(answer));
  };

  const canProceed = () => {
    const currentAnswers = selectedAnswers[currentQuestionIndex];
    return currentAnswers && currentAnswers.length > 0;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 p-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative"
        style={{ 
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }}
      >
        {/* Modal Header - Fixed */}
        <div className="bg-servify-purple text-white p-6 relative rounded-t-2xl" style={{ flexShrink: 0 }}>
          <h2 className="text-2xl font-bold text-center">{quiz.title}</h2>
          <p className="text-servify-purple/20 text-center mt-2">{quiz.description}</p>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
            data-testid="button-close-quiz"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Quiz Content - Scrollable */}
        <div 
          className="p-6" 
          data-testid="quiz-content"
          style={{ 
            flex: 1, 
            overflowY: 'auto', 
            minHeight: 0,
            maxHeight: 'calc(90vh - 120px)' // Subtract header height
          }}
        >
            {!showResults ? (
              <>
                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">
                      Question {currentQuestionIndex + 1} of {quiz.questions.length}
                    </span>
                    <span className="text-sm text-gray-600">
                      Complete all questions to finish
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-servify-purple h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentQuestionIndex + 1) / quiz.questions.length) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Question */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">{currentQuestion.question}</CardTitle>
                    <p className="text-sm text-gray-500 mt-2">
                      {(() => {
                        const correctAnswers = Array.isArray(currentQuestion.correctAnswer) 
                          ? currentQuestion.correctAnswer 
                          : [currentQuestion.correctAnswer];
                        return correctAnswers.length > 1 
                          ? "Select all correct answers (multiple selections allowed)"
                          : "Select the correct answer";
                      })()}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {currentQuestion.options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => !isAdmin && handleAnswerToggle(index)}
                          className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                            isAnswerSelected(index)
                              ? 'border-servify-purple bg-servify-purple/10 text-servify-purple'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${isAdmin ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          <div className="flex items-center">
                            {/* Checkbox style for multiple selection */}
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                              isAnswerSelected(index)
                                ? 'border-servify-purple bg-servify-purple'
                                : 'border-gray-300 bg-white'
                            }`}>
                              {isAnswerSelected(index) && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <span>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    {/* Show answer explanation for admin */}
                    {isAdmin && (
                      <div className="mt-6 p-6 bg-green-50 rounded-lg border-2 border-green-200">
                        <h4 className="font-bold text-green-800 mb-3 text-lg">✓ Correct Answer:</h4>
                        <p className="text-green-700 text-base font-medium">
                          {Array.isArray(currentQuestion.correctAnswer) 
                            ? currentQuestion.correctAnswer.map(i => currentQuestion.options[i]).join(', ')
                            : currentQuestion.options[currentQuestion.correctAnswer] || 'N/A'
                          }
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Navigation - Simplified for admin */}
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentQuestionIndex === 0}
                    title={currentQuestionIndex === 0 ? "This is the first question" : "Go to previous question"}
                  >
                    Previous
                  </Button>
                  
                  {!isAdmin ? (
                    <Button
                      onClick={handleNextQuestion}
                      disabled={!canProceed()}
                      className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                      title={!canProceed() ? "Please select at least one answer" : isLastQuestion ? "Submit your quiz answers" : "Go to next question"}
                    >
                      {isLastQuestion ? 'Submit Quiz' : 'Next Question'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                      disabled={isLastQuestion}
                      className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                      title={isLastQuestion ? "This is the last question" : "Go to next question"}
                    >
                      Next Question
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* Results */
              <div className="text-center">
                <div className="mb-6">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  
                  <h3 className="text-2xl font-bold mb-2">
                    Quiz Completed!
                  </h3>
                  <p className="text-lg text-gray-600 mb-4">
                    Your Score: <span className="font-bold">{quizScore}%</span>
                  </p>
                </div>

                {/* Question Review */}
                <div className="space-y-4 mb-6">
                  {quiz.questions.map((question, index) => {
                    const userAnswers = selectedAnswers[index] || [];
                    const correctAnswers = Array.isArray(question.correctAnswer) ? question.correctAnswer : [question.correctAnswer];
                    const isCorrect = userAnswers.length === correctAnswers.length &&
                      userAnswers.every(answer => correctAnswers.includes(answer));
                    
                    return (
                      <Card key={index} className={`border-2 ${isCorrect ? 'border-green-200' : 'border-red-200'}`}>
                        <CardHeader>
                          <CardTitle className="text-sm flex items-center">
                            {isCorrect ? (
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 mr-2" />
                            )}
                            Question {index + 1}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm mb-3">{question.question}</p>
                          <div className="space-y-2">
                            {question.options.map((option, optionIndex) => {
                              const isUserSelected = userAnswers.includes(optionIndex);
                              const isCorrectAnswer = correctAnswers.includes(optionIndex);
                              
                              return (
                                <div
                                  key={optionIndex}
                                  className={`p-2 rounded text-sm ${
                                    isCorrectAnswer
                                      ? 'bg-green-100 text-green-800'
                                      : isUserSelected
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-50'
                                  }`}
                                >
                                  {option}
                                  {isCorrectAnswer && <span className="ml-2 text-green-600">✓ Correct</span>}
                                  {isUserSelected && !isCorrectAnswer && <span className="ml-2 text-red-600">✗ Your Answer</span>}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Admin Mode - Show All Answers */}
                {isAdmin && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">Admin View - All Correct Answers:</h4>
                    {quiz.questions.map((question, index) => {
                      const correctAnswers = Array.isArray(question.correctAnswer) ? question.correctAnswer : [question.correctAnswer];
                      return (
                        <div key={index} className="text-sm text-blue-700 mb-1">
                          <strong>Q{index + 1}:</strong> {correctAnswers.map(i => question.options[i]).join(', ')}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={resetQuiz}
                    className="flex items-center"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retake Quiz
                  </Button>
                  
                  <Button
                    onClick={onClose}
                    className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}